import { Box, Title } from "@qws-org/ui-kit";
import type { FC } from "react";
import {
  data,
  type LoaderFunction,
  type MetaFunction,
  Outlet,
} from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import type { PageSegment } from "~/core";
import { getBreadcrumbsHomeLabel } from "~/core/transformers/breadcrumb-list.transformer";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import { sdkContext, settingsContext } from "~/server/get-load-context";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";

import type { Route } from "./+types/$lang.info";

const infoValidator: ObjectSchema<{
  lang: string;
  segment: PageSegment;
  slug: string;
}> = object({
  lang: string().required(),
  slug: string().required(),
  segment: string()
    .oneOf(["info", "promotions", "tournaments"])
    .nullable()
    .default(null),
});

const getPathSegments = (requestUrl: string): Array<string> => {
  return new URL(requestUrl).pathname
    .split("/")
    .filter(Boolean)
    .map((segment) =>
      segment.endsWith(".data") ? segment.slice(0, -".data".length) : segment,
    );
};

export const loader = (async ({ context, params, request }) => {
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const pathSegments = getPathSegments(request.url);
  const isInfoIndex =
    pathSegments.length === 2 &&
    pathSegments[0] === params.lang &&
    pathSegments[1] === "info";

  if (!isInfoIndex) {
    return data(
      {
        params,
        pageData: undefined,
        originalUrl: getOriginalUrl(request.url),
        languages: settings?.languages,
        lang: params?.lang ?? "",
      },
      {
        headers: {
          "Cache-Control": "public, max-age=300",
        },
      },
    );
  }

  const { slug, segment, lang } = infoValidator.validateSync({
    ...params,
    slug: settings?.app?.info?.slug,

    segment: settings?.app?.info?.segment,
  });

  const { page } = await loadPagesByConstructor(sdk, "info", lang, {
    slug,
    segment,
    pageList: "never",
  });

  return data(
    {
      params,
      pageData: page,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
      lang: params?.lang ?? "",
    },
    {
      headers: {
        "Cache-Control": "public, max-age=300",
      },
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({
  data: pageData,
  params,
  matches,
}) => {
  const constructorData = pageData?.pageData;
  return prepareSeoData({
    pageSeo: constructorData?.page.seo,
    fallbackSeo: constructorData?.fallbackSeo?.seo,
    originalUrl: `${pageData?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: pageData?.languages ?? [],
    matches,
    breadcrumbs: constructorData
      ? { data: constructorData, homeLabel: getBreadcrumbsHomeLabel(matches) }
      : undefined,
  });
};

const Page: FC<Route.ComponentProps> = ({ loaderData: { pageData } }) => {
  return (
    <Box m={{ mx: "auto" }} maxWidth={1240}>
      {pageData?.page?.preview_translations?.[0]?.title && (
        <Title
          as="h1"
          display="hidden"
          d={{ display: "block" }}
          fontSize="title.desktop.large"
          lineHeight="title.desktop.large"
          fontWeight="desktop.semibold"
          m={{ my: 64 }}
        >
          {pageData.page.preview_translations[0].title}
        </Title>
      )}
      <Outlet />
    </Box>
  );
};

export default Page;
